import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { CompanyCard } from "../../../common/u1/CompanyCard";
import { FilterChips } from "../../../common/u1/FilterChips";
import { filterCompanies, CLAIMED_FILTERS } from "../../../common/u1/filters";
import { Pagination } from "../../../common/u1/Pagination";
import { useGetIndividualQuery } from "../../../../store/Company/companyApiSlice";
import { ClaimFirstEmptyState } from "../ClaimFirstEmptyState";
import { HomeIcon } from "../../../../assets/ui/icons";
import { NoticeBox } from "../membership/NoticeBox";
import { CommonToast } from "../../../common/Toast";
import { useMembershipReturn } from "./useMembershipReturn";

/* The businesses a user manages. */
/* Ten while the list is paged here. Once the endpoint pages, this becomes
   whatever the server returns. */
export const PAGE_SIZE = 10;

export const MyCompaniesList = () => {
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.auth);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, refetch } = useGetIndividualQuery({
    individualID: userInfo?.individual?.[0]?.individualID,
    token,
  });

  /* TIP-662: landing back from the membership checkout.
   *
   * `refetch` exists only because of this — the list is otherwise content
   * with whatever the cache holds. The membership is provisioned from the
   * Stripe webhook after the buyer is already here, so the copy loaded on
   * arrival can show the company as a non-member; re-reading once
   * provisioning lands is what updates the card without a reload. */
  const announce = useCallback(() => {
    refetch();
  }, [refetch]);

  const { phase } = useMembershipReturn({ onProvisioned: announce });

  /* The toast fires from render-phase state rather than an effect of its
     own: `phase` only ever reaches "done" once, and the hook has already
     stripped the URL params by then, so a refresh cannot replay it. */
  const [announced, setAnnounced] = useState(false);
  if (phase === "done" && !announced) {
    setAnnounced(true);
    CommonToast.success("You are now a TREVEX Member");
  }

  /* Transcribed side effect: the id is read back by other screens. */
  if (data?.individualID) {
    localStorage.setItem("userindividualID", data.individualID);
  }

  const claimed = useMemo(
    () => (data?.individual || []).filter((c) => c?.companyID?.claimed),
    [data]
  );

  /* The card reads a company, not the join row the endpoint returns. */
  const companies = useMemo(() => {
    /* ONE CARD PER BUSINESS. The endpoint returns the IndComp JOIN, and it holds repeats: this account has 25 claimed rows for 13 companies — one appears six... */
    const byId = new Map();
    /* IndComp carries no timestamp, so its auto-increment key is the only
       thing that says which claim came last. Kept beside the cards rather
       than on them: the card renders a company, and ind_compID belongs to
       the join row. */
    const recency = new Map();
    claimed.forEach((row) => {
      const company = row.companyID;
      const id = company?.companyID || company?.wb_win;
      if (!id) return;

      const existing = byId.get(id);
      const status =
        row.profile_status === "member" || existing?.profile_status === "member"
          ? "member"
          : existing?.profile_status ?? row.profile_status;

      byId.set(id, { ...(existing || {}), profile_status: status, ...company });
      /* Spreading the company last would drop the status the company itself
         does not carry, so it is re-applied. */
      byId.get(id).profile_status = status;

      /* A company with several join rows is as recent as its newest one. */
      recency.set(id, Math.max(recency.get(id) ?? 0, Number(row.ind_compID) || 0));
    });

    /* Newest claim first. Someone arriving from the claim they just finished
       must find that business at the top rather than hunting for it on the
       last page — the list pages at ten. Sort is stable, so rows the endpoint
       returns without a usable key keep the order it sent them in. */
    return [...byId.entries()]
      .sort(([a], [b]) => (recency.get(b) ?? 0) - (recency.get(a) ?? 0))
      .map(([, company]) => company);
  }, [claimed]);

  const filtered = useMemo(() => filterCompanies(companies, filter), [companies, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  /* Clamped during render: narrowing the filter can leave the current page
     past the end, and correcting it in an effect would paint an empty list
     first. */
  const current = Math.min(page, totalPages);
  const start = (current - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);

  const changeFilter = (next) => {
    setFilter(next);
    setPage(1);
  };

  return (
    <section>
      {phase !== "idle" && (
        <div className="mb-4">
          {phase === "waiting" && (
            <NoticeBox tone="pending" title="Payment received">
              Activating your membership. This takes a few seconds.
            </NoticeBox>
          )}
          {phase === "done" && (
            <NoticeBox tone="done" title="You are now a TREVEX Member">
              Your membership is active. You can request verification for this
              business from its membership page.
            </NoticeBox>
          )}
          {/* Not an error: the payment went through either way, and saying
              so is the whole point of the state. */}
          {phase === "slow" && (
            <NoticeBox tone="pending" title="Payment received">
              Your payment went through. Your membership may take a minute to
              appear here.
            </NoticeBox>
          )}
        </div>
      )}

      {/* Pinned beneath the 78px top bar, so the filter a long list is being
          read through stays reachable instead of scrolling away above it.
          Below the bar's z-30 — the bar wins where they meet. */}
      <div className="sticky top-[78px] z-20 flex flex-wrap items-center justify-between gap-3 bg-[var(--bg)] pb-3">
        <h2 className="text-[19px] font-bold text-[var(--ink)] font-sans m-0">Your businesses</h2>
        <FilterChips value={filter} onChange={changeFilter} filters={CLAIMED_FILTERS} />
      </div>

      {filtered.length === 0 ? (
        companies.length === 0 ? (
          /* A newly registered account, not a filter that matched nothing —
             the design gives it the full find-your-business state rather than
             one grey sentence saying the list is empty. */
          <ClaimFirstEmptyState
            className="mt-1"
            icon={<HomeIcon size={30} />}
            title="You have not claimed a business yet."
          >
            Most UAE businesses are already on TREVEX, built from public
            licensing and registry data. Find yours and claim it for free. If it
            is not here, list it in about three minutes.
          </ClaimFirstEmptyState>
        ) : (
          <p className="py-10 text-center text-[14px] text-[var(--muted)] font-sans">
            No businesses match this filter.
          </p>
        )
      ) : (
        <>
          <div className="mt-1 flex flex-col gap-3">
            {visible.map((company) => (
              <CompanyCard
                key={company.companyID || company.wb_win}
                company={company}
                actionLabel="Manage"
                /* Where the current card's pen button goes — the company edit
                   screen. Not a new destination. */
                onAction={(c) => navigate(`/edit/${c.companyID}`)}
              />
            ))}
          </div>

          <Pagination
            page={current}
            totalPages={totalPages}
            total={filtered.length}
            pageSize={PAGE_SIZE}
            itemLabel="business"
            itemLabelPlural="businesses"
            onChange={setPage}
            label="Businesses pages"
          />
        </>
      )}
    </section>
  );
};

export default MyCompaniesList;
